import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, X } from "lucide-react";
import {
  REFINE_SNOOZE_MS, isRefineBannerVisible, refineBannerMode, type OnboardingStatusResponse,
} from "@/lib/strategyRefine";

// "Strategie schärfen" banner on top of the cockpit (plan Rev. 3, Shade option A 04.10.). The first strategy
// is generated automatically and counts as preliminary until the hotel answers the foundation questions in
// the A-Team Briefing. Visible while GET /api/onboarding/status reports strategy.refinement === "preliminary";
// while a refinement runs (refining) it only says so. "Später" hides it for a few days, then it comes back.

const snoozeKey = (userId: string) => `strategyRefineSnoozedUntil:${userId}`;

function readSnooze(userId: string): number {
  try {
    return Number(window.localStorage.getItem(snoozeKey(userId))) || 0;
  } catch {
    return 0;
  }
}

export function StrategyRefineBanner({ userId }: { userId: string }) {
  const [snoozedUntil, setSnoozedUntil] = useState(() => readSnooze(userId));
  const { data } = useQuery<OnboardingStatusResponse>({
    queryKey: ["/api/onboarding/status"],
    refetchInterval: (q) => (refineBannerMode(q.state.data) === "refining" ? 60_000 : false),
    retry: false,
  });
  const mode = refineBannerMode(data);
  if (!isRefineBannerVisible(mode, snoozedUntil, Date.now())) return null;

  const snooze = () => {
    const until = Date.now() + REFINE_SNOOZE_MS;
    try { window.localStorage.setItem(snoozeKey(userId), String(until)); } catch { /* still hidden for this visit */ }
    setSnoozedUntil(until);
  };

  if (mode === "refining") {
    return (
      <div className="mb-6 flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900" data-testid="banner-strategy-refining">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
        <span>Ihre Strategie wird gerade geschärft. Die neue Version ist in etwa 10 Minuten fertig.</span>
      </div>
    );
  }

  return (
    <div className="mb-6 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center" data-testid="banner-strategy-refine">
      <Sparkles className="hidden h-5 w-5 shrink-0 text-amber-600 sm:block" aria-hidden="true" />
      <p className="flex-1 text-sm text-amber-900">
        Ihre erste vorläufige Strategie steht. Mit drei kurzen Fragen zu Ihren Gästen wird sie deutlich treffender.
      </p>
      <div className="flex items-center gap-2">
        <Button asChild size="sm" data-testid="button-strategy-refine">
          <Link href="/a-team-briefing#strategie-schaerfen">Strategie schärfen</Link>
        </Button>
        <Button variant="ghost" size="sm" onClick={snooze} className="text-amber-900" data-testid="button-strategy-refine-later">
          <X className="mr-1 h-4 w-4" aria-hidden="true" />
          Später
        </Button>
      </div>
    </div>
  );
}
